import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UploadProgress {
  fileName: string;
  progress: number;
  status: "pending" | "uploading" | "complete" | "error";
  error?: string;
  url?: string;
}

interface UseFileUploadOptions {
  bucket?: string;
  maxSizeMB?: number;
  allowedTypes?: string[];
  onUploadComplete?: (files: UploadProgress[]) => void;
}

export function useFileUpload(options: UseFileUploadOptions = {}) {
  const {
    bucket = "attachments",
    maxSizeMB = 10,
    allowedTypes,
    onUploadComplete,
  } = options;

  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const validateFile = useCallback(
    (file: File): string | null => {
      const maxBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        return `File size exceeds ${maxSizeMB}MB limit`;
      }

      if (allowedTypes && allowedTypes.length > 0) {
        const isAllowed = allowedTypes.some((type) => {
          if (type.endsWith("/*")) {
            const category = type.split("/")[0];
            return file.type.startsWith(category + "/");
          }
          return file.type === type;
        });

        if (!isAllowed) {
          return `File type ${file.type} is not allowed`;
        }
      }

      return null;
    },
    [maxSizeMB, allowedTypes]
  );

  const uploadFile = useCallback(
    async (file: File, recordId: string, fieldId: string): Promise<UploadProgress> => {
      const validationError = validateFile(file);
      if (validationError) {
        return {
          fileName: file.name,
          progress: 0,
          status: "error",
          error: validationError,
        };
      }

      const fileExt = file.name.split(".").pop()?.toLowerCase() || "";
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 8);
      const filePath = `${recordId}/${fieldId}/${timestamp}-${randomStr}.${fileExt}`;

      try {
        const { data, error } = await supabase.storage
          .from(bucket)
          .upload(filePath, file, {
            cacheControl: "3600",
            upsert: false,
          });

        if (error) throw error;

        const { data: urlData } = supabase.storage
          .from(bucket)
          .getPublicUrl(data.path);

        return {
          fileName: file.name,
          progress: 100,
          status: "complete",
          url: urlData.publicUrl,
        };
      } catch (error: any) {
        console.error("Upload error:", error);
        return {
          fileName: file.name,
          progress: 0,
          status: "error",
          error: error.message || "Upload failed",
        };
      }
    },
    [bucket, validateFile]
  );

  const uploadFiles = useCallback(
    async (files: File[], recordId: string, fieldId: string) => {
      setIsUploading(true);
      
      const initialUploads: UploadProgress[] = files.map((file) => ({
        fileName: file.name,
        progress: 0,
        status: "pending" as const,
      }));
      setUploads(initialUploads);

      const results: UploadProgress[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        setUploads((prev) =>
          prev.map((u, idx) =>
            idx === i ? { ...u, status: "uploading" as const, progress: 50 } : u
          )
        );

        const result = await uploadFile(file, recordId, fieldId);
        results.push(result);

        setUploads((prev) =>
          prev.map((u, idx) => (idx === i ? result : u))
        );
      }

      setIsUploading(false);
      onUploadComplete?.(results);

      const successCount = results.filter((r) => r.status === "complete").length;
      const errorCount = results.filter((r) => r.status === "error").length;

      if (errorCount > 0) {
        toast.error(`${errorCount} file(s) failed to upload`);
      }
      if (successCount > 0) {
        toast.success(`${successCount} file(s) uploaded successfully`);
      }

      return results;
    },
    [uploadFile, onUploadComplete]
  );

  const deleteFile = useCallback(
    async (filePath: string) => {
      try {
        const { error } = await supabase.storage.from(bucket).remove([filePath]);
        if (error) throw error;
        return true;
      } catch (error: any) {
        console.error("Delete error:", error);
        toast.error("Failed to delete file");
        return false;
      }
    },
    [bucket]
  );

  const clearUploads = useCallback(() => {
    setUploads([]);
  }, []);

  return {
    uploads,
    isUploading,
    uploadFiles,
    uploadFile,
    deleteFile,
    clearUploads,
    validateFile,
  };
}
